import React from 'react';
import { Icon, VStack } from '@chakra-ui/react';
import { LuInfo } from 'react-icons/lu';
import { DocumentWithDownloadUrl as Document } from '@/client';
import CollapsibleCard from '@/components/common/CollapsibleCard';
import CollapsibleCardDetailItem from '@/components/common/CollapsibleCardDetailItem';
import { getFileExtension, isFile, isImportant } from '@/utils/documentUtils';
import { formatFileSize } from '@/utils/fileUtils';
import { formatDate } from '@/utils/dateUtils';
import { useHouseMemberNames } from '@/hooks/sharing/useHouseMemberNames';

interface DocumentDetailsProps {
	document: Document;
}

const getFileTypeDisplay = (document: Document): string => {
	const extension = getFileExtension(document);
	if (!extension) return 'File';

	return extension.substring(1).toUpperCase();
};

const DocumentDetails: React.FC<DocumentDetailsProps> = ({ document }) => {
	// Above the early return, since a hook cannot be called conditionally.
	const resolveMemberName = useHouseMemberNames(document.house_id);

	if (isImportant(document)) return null;

	const formattedDate = formatDate(document.created_at);
	// Empty rather than a guess when the uploader has since lost access to the house.
	const uploadedBy = resolveMemberName(document.uploaded_by_id) ?? '';

	return (
		<CollapsibleCard
			title="Details"
			icon={<Icon as={LuInfo} boxSize={5} color="brand.fg" />}
			value="details"
			defaultOpen={false}
			autoScrollOnOpen={true}
		>
			<VStack align="stretch" gap={3} px={0}>
				{isFile(document) && (
					<CollapsibleCardDetailItem label="File type" value={getFileTypeDisplay(document)} />
				)}
				{/* Always present, empty where the record carries no size: a row that comes and goes
				    moves the ones below it, and its absence says nothing the empty value does not. */}
				<CollapsibleCardDetailItem label="Size" value={formatFileSize(document.size_bytes)} />
				<CollapsibleCardDetailItem label="Added" value={formattedDate} />
				<CollapsibleCardDetailItem label="Added by" value={uploadedBy} />
			</VStack>
		</CollapsibleCard>
	);
};

export default DocumentDetails;
